import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Linkedin, ExternalLink } from 'lucide-react';

/**
 * Reusable Footer component with SEO-optimized internal links
 */
export default function Footer() {
  const pillarPages = [
    { label: 'Veille marchés publics', href: '/veille-marches-publics' },
    { label: 'Trouver appels d\'offres', href: '/trouver-appels-offres' },
    { label: 'Analyse DCE', href: '/analyse-dce' },
    { label: 'Plateforme appels d\'offres', href: '/plateforme-appels-offres' },
    { label: 'Répondre aux marchés publics', href: '/repondre-marches-publics' },
  ];

  const guides = [
    { label: 'Comment trouver des appels d\'offres', href: '/blog/comment-trouver-appels-offres' },
    { label: 'Comment lire un DCE rapidement', href: '/blog/comment-lire-dce-rapidement' },
    { label: 'Comment analyser un DCE', href: '/blog/comment-analyser-dce' },
    { label: 'Comment répondre à un appel d\'offres', href: '/blog/comment-repondre-appel-offres' },
    { label: 'Comment rédiger un mémoire technique', href: '/blog/comment-rediger-memoire-technique' },
  ];

  const sectorPages = [
    { label: 'Marchés publics BTP', href: '/marches-publics-btp' },
    { label: 'Marchés publics Sécurité', href: '/marches-publics-securite' },
    { label: 'Marchés publics IT', href: '/marches-publics-informatique' },
    { label: 'Marchés publics Nettoyage', href: '/marches-publics-nettoyage' },
    { label: 'Marchés publics Formation', href: '/marches-publics-formation' },
  ];

  const resources = [
    { label: 'Blog', href: '/blog' },
    { label: 'Guide complet', href: '/guide-appels-offres' },
    { label: 'Synthèse IA', href: '/analyse-dce' },
    { label: 'Analyse gratuite', href: '/analyse-gratuite' },
  ];

  const legal = [
    { label: 'Mentions légales', href: '/legal' },
    { label: 'Contact', href: '/contact' },
  ];

  const partnerLinks = [
    { label: 'Assistance Appel d\'Offre', href: 'https://assistance-appel-offre.fr/', external: true },
  ];

  return (
    <footer className="bg-slate-900 text-slate-400 py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white">Mon DCE Express</span>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Veille marchés publics et analyse DCE par IA.
            </p>
            {/* LinkedIn Link */}
            <a 
              href="https://www.linkedin.com/company/43274769" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="footer-linkedin-link"
            >
              <Linkedin className="w-5 h-5" />
              <span>Suivez-nous sur LinkedIn</span>
            </a>
          </div>

          {/* Pillar Pages */}
          <div>
            <h4 className="font-semibold text-white mb-4">Pages piliers</h4>
            <ul className="space-y-2 text-sm">
              {pillarPages.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Sector Pages */}
          <div>
            <h4 className="font-semibold text-white mb-4">Par secteur</h4>
            <ul className="space-y-2 text-sm">
              {sectorPages.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Guides SEO */}
          <div>
            <h4 className="font-semibold text-white mb-4">Guides pratiques</h4>
            <ul className="space-y-2 text-sm">
              {guides.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-white mb-4">Ressources</h4>
            <ul className="space-y-2 text-sm">
              {resources.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-white mb-4">Légal</h4>
            <ul className="space-y-2 text-sm">
              {legal.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Partner Links */}
            <h4 className="font-semibold text-white mb-4 mt-6">Partenaires</h4>
            <ul className="space-y-2 text-sm">
              {partnerLinks.map((item) => (
                <li key={item.href}>
                  <a 
                    href={item.href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    {item.label}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-slate-500">
            Sources: BOAMP • DECP • TED Europa • SNCF • ADP • Dematis
          </div>
          <div className="text-sm">
            © 2026 Mon DCE Express. Tous droits réservés.
          </div>
        </div>
      </div>
    </footer>
  );
}
